import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useMemo } from "react";
import { isWebGPUSupported } from "@/lib/utils/webgpu";

export type GraphicsQuality = "low" | "medium" | "high";
export type RenderBackend = "webgl" | "webgpu";

interface GraphicsState {
  // Qualidade geral
  quality: GraphicsQuality;
  
  // Render backend (WebGL ou WebGPU experimental)
  renderBackend: RenderBackend;
  webgpuAvailable: boolean;
  
  // Customização inteligente inicial (Auto-Detector WebGL/CPU/Hz)
  hasAutoDetected: boolean;
  
  // Performance
  targetFps: number;
  dprScale: number; // 0.5 a 2.0 (multiplier do device pixel ratio)
  showFpsMonitor: boolean;
  showTensionMonitor: boolean; // Monitor de tensão/intensidade da música
  
  // UI Settings
  showBottomBar: boolean; // Mostrar barra inferior no menu
  
  // Otimização dinâmica baseada no número de bots
  dynamicOptimization: boolean;
  currentBotCount: number;
  /**
   * Faixa de otimização em vigor. TRAVA no pior caso visto desde o último
   * `resetOptimizationTier()` (começo de onda/partida).
   *
   * Sem a trava, o tier caía junto com os bots mortos e reativava iluminação
   * avançada / pós-processamento NO MEIO da partida — cada volta dessas troca a
   * contagem de luzes ou recria o composer, o que invalida os programas GLSL de
   * toda a cena e produz exatamente o engasgo que o ajuste queria evitar.
   */
  optimizationTier: number;
  
  // Explosões
  advancedExplosions: boolean;
  explosionParticles: boolean;
  explosionLights: boolean;
  explosionShockwave: boolean;
  
  // Iluminação
  advancedLighting: boolean;
  softShadows: boolean;
  shadowQuality: "low" | "medium" | "high"; // 512, 1024, 2048
  ambientOcclusion: boolean;
  
  // Efeitos visuais
  bulletGlow: boolean;
  bulletTrails: boolean;
  screenShake: boolean;

  // Pós-processamento (bloom neon + vinheta) — só para GPUs com folga
  postProcessing: boolean;

  // Resolução dinâmica: reduz o DPR quando o FPS cai, devolve quando sobra
  dynamicResolution: boolean;

  // Setters
  setQuality: (quality: GraphicsQuality) => void;
  setRenderBackend: (backend: RenderBackend) => void;
  setTargetFps: (fps: number) => void;
  setDprScale: (scale: number) => void;
  setShowFpsMonitor: (show: boolean) => void;
  setShowTensionMonitor: (show: boolean) => void;
  setShowBottomBar: (show: boolean) => void;
  setDynamicOptimization: (enabled: boolean) => void;
  updateBotCount: (count: number) => void;
  /** Libera a trava do tier — chamar no começo de cada onda/partida. */
  resetOptimizationTier: () => void;
  setAdvancedExplosions: (enabled: boolean) => void;
  setExplosionParticles: (enabled: boolean) => void;
  setExplosionLights: (enabled: boolean) => void;
  setExplosionShockwave: (enabled: boolean) => void;
  setAdvancedLighting: (enabled: boolean) => void;
  setSoftShadows: (enabled: boolean) => void;
  setShadowQuality: (quality: "low" | "medium" | "high") => void;
  setAmbientOcclusion: (enabled: boolean) => void;
  setBulletGlow: (enabled: boolean) => void;
  setBulletTrails: (enabled: boolean) => void;
  setScreenShake: (enabled: boolean) => void;
  setPostProcessing: (enabled: boolean) => void;
  setDynamicResolution: (enabled: boolean) => void;
  setHasAutoDetected: (detected: boolean) => void;
  autoDetectHardware: () => void;
  
  // Helpers
  getShadowMapSize: () => number;
  getEffectiveDpr: () => [number, number];
  applyQualityPreset: (quality: GraphicsQuality) => void;
  getOptimizedSettings: () => Partial<GraphicsState>;
}

export const useGraphics = create<GraphicsState>()(
  persist(
    (set, get) => ({
      // Defaults - High quality
      quality: "high",
      
      // Render backend
      renderBackend: "webgl",
      webgpuAvailable: typeof navigator !== "undefined" && isWebGPUSupported(),
      
      // Auto-Detect state
      hasAutoDetected: false,

      // Performance
      targetFps: 60,
      dprScale: 1.0,
      showFpsMonitor: false,
      showTensionMonitor: false,
      
      // UI Settings
      showBottomBar: true,
      
      // Otimização dinâmica
      dynamicOptimization: true,
      currentBotCount: 0,
      optimizationTier: 0,

      advancedExplosions: true,
      explosionParticles: true,
      explosionLights: true,
      explosionShockwave: true,
      
      advancedLighting: true,
      softShadows: true,
      shadowQuality: "high",
      ambientOcclusion: false, // Desativado por padrão (custo alto)
      
      bulletGlow: true,
      bulletTrails: true,
      screenShake: true,

      postProcessing: false, // Ativado pelo auto-detect/preset em GPUs fortes
      dynamicResolution: true,

      // Setters
      setQuality: (quality) => {
        set({ quality });
        get().applyQualityPreset(quality);
      },
      
      setRenderBackend: (backend) => {
        if (backend === "webgpu" && !get().webgpuAvailable) {
          console.warn("[Graphics] WebGPU not available, keeping WebGL");
          return;
        }
        set({ renderBackend: backend });
        console.log(`[Graphics] Render backend set to: ${backend}`);
      },
      
      setTargetFps: (fps) => set({ targetFps: fps }),
      setDprScale: (scale) => set({ dprScale: Math.max(0.5, Math.min(2.0, scale)) }),
      setShowFpsMonitor: (show) => set({ showFpsMonitor: show }),
      setShowTensionMonitor: (show) => set({ showTensionMonitor: show }),
      setShowBottomBar: (show) => set({ showBottomBar: show }),
      setDynamicOptimization: (enabled) => set({ dynamicOptimization: enabled }),
      updateBotCount: (count) => {
        const { currentBotCount, optimizationTier } = get();
        const tier = Math.max(optimizationTier, optimizationTierForBots(count));
        if (count === currentBotCount && tier === optimizationTier) return;
        set({ currentBotCount: count, optimizationTier: tier });
      },
      resetOptimizationTier: () => {
        if (get().optimizationTier !== 0) set({ optimizationTier: 0 });
      },
      
      setAdvancedExplosions: (enabled) => set({ advancedExplosions: enabled }),
      setExplosionParticles: (enabled) => set({ explosionParticles: enabled }),
      setExplosionLights: (enabled) => set({ explosionLights: enabled }),
      setExplosionShockwave: (enabled) => set({ explosionShockwave: enabled }),
      setAdvancedLighting: (enabled) => set({ advancedLighting: enabled }),
      setSoftShadows: (enabled) => set({ softShadows: enabled }),
      setShadowQuality: (quality) => set({ shadowQuality: quality }),
      setAmbientOcclusion: (enabled) => set({ ambientOcclusion: enabled }),
      setBulletGlow: (enabled) => set({ bulletGlow: enabled }),
      setBulletTrails: (enabled) => set({ bulletTrails: enabled }),
      setScreenShake: (enabled) => set({ screenShake: enabled }),
      setPostProcessing: (enabled) => set({ postProcessing: enabled }),
      setDynamicResolution: (enabled) => set({ dynamicResolution: enabled }),
      setHasAutoDetected: (detected) => set({ hasAutoDetected: detected }),
      
      // Motor de Benchmarking passivo ao inicializar o app
      autoDetectHardware: () => {
        if (typeof window === "undefined" || typeof document === "undefined") return;

        let hardwareConcur = 4;
        try {
          if (navigator.hardwareConcurrency) {
            hardwareConcur = navigator.hardwareConcurrency;
          }
        } catch (_e) {
          console.warn("[Graphics] Could not read hardware concurrency.");
        }

        let gpuName = "Unknown";
        try {
          const canvas = document.createElement("canvas");
          const gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
          if (gl) {
            // @ts-ignore
            const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
            if (debugInfo) {
              // @ts-ignore
              gpuName = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
            }
          }
        } catch (_e) {
          console.warn("[Graphics] Could not retrieve unmasked WebGL renderer info.");
        }

        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        const lowerGpu = gpuName.toLowerCase();

        // 1. Decidindo o Preset de Qualidade Geral!
        let recommendedQuality: GraphicsQuality = "high"; // Optimistic default para Desktop genérico

        if (isMobile) {
          if (hardwareConcur <= 4 || lowerGpu.includes("mali-t") || lowerGpu.includes("powervr")) {
            recommendedQuality = "low";
          } else if (hardwareConcur >= 8 && (lowerGpu.includes("adreno 7") || lowerGpu.includes("adreno 8") || lowerGpu.includes("apple a15") || lowerGpu.includes("apple a16") || lowerGpu.includes("apple a17"))) {
            // Mobile top tier
            recommendedQuality = "high";
          } else {
            recommendedQuality = "medium";
          }
        } else {
          // Desktops/Laptops
          if (lowerGpu.includes("mali") || lowerGpu.includes("intel hd") || lowerGpu.includes("intel uhd") || hardwareConcur <= 4 || lowerGpu.includes("swiftshader") || lowerGpu.includes("llvmpipe")) {
            recommendedQuality = "low"; // Notebook genérico de trabalho e renderizadores em software (VM)
          } else if (lowerGpu.includes("nvidia") || lowerGpu.includes("radeon") || lowerGpu.includes("apple m1") || lowerGpu.includes("apple m2") || lowerGpu.includes("apple m3") || lowerGpu.includes("apple m4")) {
            // Placas Dedicadas + Macs Modernos = Sobra performance.
            recommendedQuality = "high";
          } else {
            // IGPus medianas
            recommendedQuality = "medium";
          }
        }

        // 2. Aplicando a Configuração Sugerida
        set({ quality: recommendedQuality, hasAutoDetected: true });
        get().applyQualityPreset(recommendedQuality);

        // Bloom fica off por padrão em mobile mesmo no preset high — o custo
        // de fill-rate do composer pesa mais em telas de celular. O jogador
        // pode ligar manualmente nas opções.
        if (isMobile) {
          set({ postProcessing: false });
        }
        
        console.log(`[Graphics] Auto-Detect Applied: '${recommendedQuality}' presets for GPU: ${gpuName} (${hardwareConcur} cores).`);

        // 3. Destravamento Automático de Tela de 120Hz/144Hz
        // Nós mediremos o tempo (delta) entre callbacks nativos que a Tela nos dá para saber se o cara tem High Refresh Rate.
        let frames = 0;
        let lastTime = typeof window !== "undefined" ? window.performance.now() : Date.now();
        const testDuration = 500; // Analisar meio segundo

        const checkRefreshRate = (now: number) => {
          frames++;
          if (now - lastTime < testDuration) {
            requestAnimationFrame(checkRefreshRate);
          } else {
            // Calcular os frames por segundo projetados nesse período
            const fpsMeasured = (frames / testDuration) * 1000;
            let targetHz = 60;
            
            if (fpsMeasured > 105) { // Passou da margem de erro, o monitor roda acima de 120hz com certeza
              targetHz = 144; // Libera limite máximo disponível pra garantir fluidez
              console.log(`[Graphics] Ultra High Refresh Rate Display Detected (~${Math.round(fpsMeasured)}Hz)! Target FPS unlocked to 144.`);
            } else if (fpsMeasured > 75) {
              targetHz = 90;
              console.log(`[Graphics] High Refresh Rate Display Detected (~${Math.round(fpsMeasured)}Hz)! Target FPS set to 90.`);
            } else {
              console.log(`[Graphics] Standard 60Hz Display Detected (~${Math.round(fpsMeasured)}Hz). Target FPS capped at 60.`);
            }
            // Seta em Background
            set({ targetFps: targetHz });
          }
        };

        requestAnimationFrame((now) => {
          lastTime = now;
          requestAnimationFrame(checkRefreshRate);
        });
      },
      
      // Helpers
      getShadowMapSize: () => {
        const { shadowQuality } = get();
        switch (shadowQuality) {
          case "low": return 512;
          case "medium": return 1024;
          case "high": return 2048;
          default: return 1024;
        }
      },
      
      getEffectiveDpr: () => {
        const { dprScale, targetFps } = get();
        const baseDpr = typeof window !== "undefined" ? window.devicePixelRatio : 1;
        const maxDpr = targetFps >= 120 ? 2 : 1.5;
        return [1 * dprScale, Math.min(baseDpr * dprScale, maxDpr)];
      },
      
      // Fonte única: a mesma tabela usada por useOptimizedGraphics(), já com o
      // tier travado (não recalcula por bot morto).
      getOptimizedSettings: () => {
        const state = get();
        if (!state.dynamicOptimization) return {};
        return OPTIMIZATION_TIERS[state.optimizationTier] ?? {};
      },
      
      applyQualityPreset: (quality) => {
        switch (quality) {
          case "low":
            set({
              advancedExplosions: false,
              explosionParticles: false,
              explosionLights: false,
              explosionShockwave: false,
              advancedLighting: false,
              softShadows: false,
              shadowQuality: "low",
              ambientOcclusion: false,
              bulletGlow: false,
              bulletTrails: false,
              screenShake: true,
              postProcessing: false,
            });
            break;
          case "medium":
            set({
              advancedExplosions: true,
              explosionParticles: true,
              explosionLights: false,
              explosionShockwave: false,
              advancedLighting: true,
              softShadows: true,
              shadowQuality: "medium",
              ambientOcclusion: false,
              bulletGlow: true,
              bulletTrails: false,
              screenShake: true,
              postProcessing: false,
            });
            break;
          case "high":
            set({
              advancedExplosions: true,
              explosionParticles: true,
              explosionLights: true,
              explosionShockwave: true,
              advancedLighting: true,
              softShadows: true,
              shadowQuality: "high",
              ambientOcclusion: false,
              bulletGlow: true,
              bulletTrails: true,
              screenShake: true,
              postProcessing: true,
            });
            break;
        }
      },
    }),
    {
      name: "rocketanks-graphics",
      partialize: (state) => ({
        quality: state.quality,
        renderBackend: state.renderBackend,
        hasAutoDetected: state.hasAutoDetected,
        targetFps: state.targetFps,
        dprScale: state.dprScale,
        showFpsMonitor: state.showFpsMonitor,
        showTensionMonitor: state.showTensionMonitor,
        dynamicOptimization: state.dynamicOptimization,
        advancedExplosions: state.advancedExplosions,
        explosionParticles: state.explosionParticles,
        explosionLights: state.explosionLights,
        explosionShockwave: state.explosionShockwave,
        advancedLighting: state.advancedLighting,
        softShadows: state.softShadows,
        shadowQuality: state.shadowQuality,
        ambientOcclusion: state.ambientOcclusion,
        bulletGlow: state.bulletGlow,
        bulletTrails: state.bulletTrails,
        screenShake: state.screenShake,
        postProcessing: state.postProcessing,
        dynamicResolution: state.dynamicResolution,
      }),
    }
  )
);

// Overrides por faixa de bots — mesma tabela de getOptimizedSettings(),
// derivada de um "tier" primitivo para o seletor não re-renderizar a cada
// morte de bot (só quando muda de faixa).
const OPTIMIZATION_TIERS: Partial<Record<
  "explosionLights" | "explosionShockwave" | "ambientOcclusion" | "bulletTrails" |
  "softShadows" | "explosionParticles" | "advancedLighting" | "bulletGlow" |
  "advancedExplosions" | "postProcessing",
  boolean
>>[] = [
  {},
  { explosionLights: false, explosionShockwave: false, ambientOcclusion: false },
  { explosionLights: false, explosionShockwave: false, ambientOcclusion: false, bulletTrails: false, softShadows: false },
  { explosionLights: false, explosionShockwave: false, ambientOcclusion: false, bulletTrails: false, softShadows: false, explosionParticles: false, advancedLighting: false, postProcessing: false },
  { explosionLights: false, explosionShockwave: false, ambientOcclusion: false, bulletTrails: false, softShadows: false, explosionParticles: false, advancedLighting: false, bulletGlow: false, advancedExplosions: false, postProcessing: false },
];

export type OptimizedGraphics = {
  advancedExplosions: boolean;
  explosionParticles: boolean;
  explosionLights: boolean;
  explosionShockwave: boolean;
  advancedLighting: boolean;
  softShadows: boolean;
  shadowQuality: "low" | "medium" | "high";
  ambientOcclusion: boolean;
  bulletGlow: boolean;
  bulletTrails: boolean;
  screenShake: boolean;
  postProcessing: boolean;
};

/** Faixa de bots → índice em OPTIMIZATION_TIERS. */
export function optimizationTierForBots(botCount: number): number {
  if (botCount <= 5) return 0;
  if (botCount <= 10) return 1;
  if (botCount <= 20) return 2;
  if (botCount <= 30) return 3;
  return 4;
}

/**
 * Versão NÃO reativa de `useOptimizedGraphics`, para uso dentro de `useFrame`.
 * Componentes que só precisam do valor a cada frame não devem assinar a store:
 * assinar significa re-renderizar — e, num pool de efeitos, remontar meshes no
 * meio da partida.
 *
 * Escreve sempre no MESMO objeto: chamar a cada frame não gera lixo. Quem
 * guardar a referência precisa saber que ela muda de conteúdo.
 */
const optimizedSingleton: OptimizedGraphics = {
  advancedExplosions: true,
  explosionParticles: true,
  explosionLights: true,
  explosionShockwave: true,
  advancedLighting: true,
  softShadows: true,
  shadowQuality: "high",
  ambientOcclusion: false,
  bulletGlow: true,
  bulletTrails: true,
  screenShake: true,
  postProcessing: false,
};

export function resolveOptimizedGraphics(): OptimizedGraphics {
  const state = useGraphics.getState();
  const tier = state.dynamicOptimization ? state.optimizationTier : 0;
  const optimized = OPTIMIZATION_TIERS[tier] ?? {};

  optimizedSingleton.advancedExplosions = optimized.advancedExplosions ?? state.advancedExplosions;
  optimizedSingleton.explosionParticles = optimized.explosionParticles ?? state.explosionParticles;
  optimizedSingleton.explosionLights = optimized.explosionLights ?? state.explosionLights;
  optimizedSingleton.explosionShockwave = optimized.explosionShockwave ?? state.explosionShockwave;
  optimizedSingleton.advancedLighting = optimized.advancedLighting ?? state.advancedLighting;
  optimizedSingleton.softShadows = optimized.softShadows ?? state.softShadows;
  optimizedSingleton.shadowQuality = state.shadowQuality;
  optimizedSingleton.ambientOcclusion = optimized.ambientOcclusion ?? state.ambientOcclusion;
  optimizedSingleton.bulletGlow = optimized.bulletGlow ?? state.bulletGlow;
  optimizedSingleton.bulletTrails = optimized.bulletTrails ?? state.bulletTrails;
  optimizedSingleton.screenShake = state.screenShake;
  optimizedSingleton.postProcessing = optimized.postProcessing ?? state.postProcessing;

  return optimizedSingleton;
}

export function useOptimizedGraphics() {
  // [PERF] Antes: useGraphics() sem seletor (assinava a store INTEIRA) e um
  // useMemo com objeto novo nas deps — todo consumidor re-renderizava a cada
  // mudança de qualquer campo, inclusive currentBotCount a cada morte.
  const advancedExplosions = useGraphics((s) => s.advancedExplosions);
  const explosionParticles = useGraphics((s) => s.explosionParticles);
  const explosionLights = useGraphics((s) => s.explosionLights);
  const explosionShockwave = useGraphics((s) => s.explosionShockwave);
  const advancedLighting = useGraphics((s) => s.advancedLighting);
  const softShadows = useGraphics((s) => s.softShadows);
  const shadowQuality = useGraphics((s) => s.shadowQuality);
  const ambientOcclusion = useGraphics((s) => s.ambientOcclusion);
  const bulletGlow = useGraphics((s) => s.bulletGlow);
  const bulletTrails = useGraphics((s) => s.bulletTrails);
  const screenShake = useGraphics((s) => s.screenShake);
  const postProcessing = useGraphics((s) => s.postProcessing);
  const optimizationTier = useGraphics((s) => (s.dynamicOptimization ? s.optimizationTier : 0));

  return useMemo(() => {
    const optimized = OPTIMIZATION_TIERS[optimizationTier];
    return {
      advancedExplosions: optimized.advancedExplosions ?? advancedExplosions,
      explosionParticles: optimized.explosionParticles ?? explosionParticles,
      explosionLights: optimized.explosionLights ?? explosionLights,
      explosionShockwave: optimized.explosionShockwave ?? explosionShockwave,
      advancedLighting: optimized.advancedLighting ?? advancedLighting,
      softShadows: optimized.softShadows ?? softShadows,
      shadowQuality,
      ambientOcclusion: optimized.ambientOcclusion ?? ambientOcclusion,
      bulletGlow: optimized.bulletGlow ?? bulletGlow,
      bulletTrails: optimized.bulletTrails ?? bulletTrails,
      screenShake,
      postProcessing: optimized.postProcessing ?? postProcessing,
    };
  }, [
    optimizationTier, advancedExplosions, explosionParticles, explosionLights,
    explosionShockwave, advancedLighting, softShadows, shadowQuality,
    ambientOcclusion, bulletGlow, bulletTrails, screenShake, postProcessing,
  ]);
}
